<template>
  <span>
    <b-button
      v-if="show_button"
      size="sm"
      :class="`btn-ai-quick ${button_class}`"
      @click="open"
    >
      <img src="@/assets/icon-ai.svg" alt="" class="kl-ai-icon-sm mr-1" />
      {{
        rubric
          ? $t("reusable.aiRubricBuilderModal.openButtonFill", {
              entity: rubricLabel,
            })
          : $t("reusable.aiRubricBuilderModal.openButton")
      }}
    </b-button>

    <!-- `ignore-enforce-focus-selector` y no `no-enforce-focus`: es lo que deja
         escribir en la barra de IA lateral sin que el modal robe el foco. -->
    <b-modal
      v-model="visible"
      size="xl"
      dialog-class="kl-ai-dialog"
      body-class="kl-ai-body"
      footer-class="kl-ai-footer"
      ignore-enforce-focus-selector="#sidebar-right"
      :no-close-on-backdrop="busy"
      :no-close-on-esc="busy"
      @hide="confirmClose"
      @hidden="reset"
    >
      <template #modal-header>
        <div class="d-flex w-100 flex-column">
          <div class="d-flex align-items-start justify-content-between">
            <div class="d-flex align-items-start">
              <img
                src="@/assets/icon-ai.svg"
                alt=""
                class="kl-ai-icon-lg mr-2"
              />
              <div>
                <h5 class="kl-ai-title mb-0">
                  {{
                    $t("reusable.aiRubricBuilderModal.title", {
                      entity: rubricLabel,
                    })
                  }}
                </h5>
                <small class="text-muted">
                  {{
                    step === "setup"
                      ? $t("reusable.aiRubricBuilderModal.subtitleSetup")
                      : $t("reusable.aiRubricBuilderModal.subtitleReview")
                  }}
                </small>
              </div>
            </div>
            <b-button
              v-if="!busy"
              variant="link"
              size="sm"
              class="kl-ai-close p-0"
              :aria-label="$t('reusable.aiRubricBuilderModal.close')"
              @click="close"
            >
              <b-icon icon="x" font-scale="1.4"></b-icon>
            </b-button>
          </div>
          <div class="kl-ai-steps mt-2" aria-hidden="true">
            <span class="kl-ai-step kl-ai-step-done"></span>
            <span
              class="kl-ai-step"
              :class="{ 'kl-ai-step-done': step === 'review' }"
            ></span>
          </div>
        </div>
      </template>

      <b-alert :show="error != null" variant="danger" class="py-2">
        <b-icon icon="exclamation-triangle-fill" class="mr-1"></b-icon>
        {{ error }}
      </b-alert>

      <!-- ===================== Paso 1 ===================== -->
      <template v-if="step === 'setup'">
        <div v-if="piece_name" class="kl-ai-context mb-3">
          <div class="kl-ai-small">
            <b-icon icon="diagram-3" class="mr-1"></b-icon>
            {{
              $t("reusable.aiRubricBuilderModal.fromDesign", {
                piece: piece_name,
              })
            }}
          </div>
        </div>

        <!-- El nombre de la pauta. Venía con el título de la evaluación puesto
             por el sistema, que casi nunca es como se llama la pauta. Se
             propone y se puede escribir encima. -->
        <b-form-group
          v-if="!rubric"
          :label="$t('reusable.aiRubricBuilderModal.titleLabel')"
          label-cols="0"
          label-cols-sm="3"
          class="m-0 p-0 mb-2"
        >
          <b-form-input
            v-model="rubric_title"
            size="sm"
            :placeholder="$t('reusable.aiRubricBuilderModal.titlePlaceholder')"
            :disabled="busy"
          ></b-form-input>
        </b-form-group>

        <!-- La escala. Es un dato, no una propuesta: la IA escribe las celdas,
             no las columnas. Con la pauta ya creada ni se elige — son las
             columnas que esa pauta tiene. -->
        <b-form-group
          v-if="!rubric"
          :label="$t('reusable.aiRubricBuilderModal.presetLabel')"
          label-cols="0"
          label-cols-sm="3"
          class="m-0 p-0 mb-2"
        >
          <b-form-select
            v-model="preset_id"
            size="sm"
            :options="presetOptions"
            :disabled="busy"
          ></b-form-select>
        </b-form-group>

        <!-- Cómo se muestra el logro de cada nivel. Lo define el Modelo, pero es
             del usuario cambiarlo, igual que en el formulario manual. -->
        <b-form-group
          v-if="!rubric"
          :label="achievementByLabel"
          label-cols="0"
          label-cols-sm="3"
          class="m-0 p-0 mb-2"
        >
          <b-form-radio-group
            v-model="show_percentage_sign"
            size="sm"
            :options="showPercentageSigns"
            :disabled="busy"
          ></b-form-radio-group>
        </b-form-group>
        <p class="kl-ai-small text-muted mb-3">
          <template v-if="levels.length > 0">
            {{
              $t("reusable.aiRubricBuilderModal.levelsSummary", {
                count: levels.length,
              })
            }}
            <span
              v-for="level in levels"
              :key="`level-${level.order}`"
              class="kl-ai-chip"
            >
              {{ level.title }} · {{ level.score }}
            </span>
          </template>
          <template v-else>
            {{ $t("reusable.aiRubricBuilderModal.levelsMissing") }}
          </template>
        </p>

        <label class="kl-ai-label kl-ai-small">
          {{ $t("reusable.aiRubricBuilderModal.themeLabel") }}
        </label>
        <AiThemeInput
          v-model="theme"
          class="mb-3"
          :placeholder="$t('reusable.aiRubricBuilderModal.themePlaceholder')"
          :hint="$t('reusable.aiRubricBuilderModal.themeHint')"
          :info="$t('reusable.aiRubricBuilderModal.themeInfo')"
          :disabled="busy"
        ></AiThemeInput>

        <div class="kl-ai-label mb-1">
          {{
            $t("reusable.aiRubricBuilderModal.criteriaTitle", {
              entity: criteriaLabelPlural,
            })
          }}
        </div>
        <p class="kl-ai-small text-muted mb-2">
          {{
            $t("reusable.aiRubricBuilderModal.criteriaHint", {
              entity: criteriaLabel,
            })
          }}
        </p>

        <b-table-simple small bordered responsive class="kl-ai-table mb-1">
          <b-thead>
            <b-tr>
              <b-th style="width: 72%">{{ criteriaLabelPlural }}</b-th>
              <b-th style="width: 20%">
                {{ $t("reusable.aiRubricBuilderModal.colWeighing") }}
              </b-th>
              <b-th style="width: 8%"></b-th>
            </b-tr>
          </b-thead>
          <b-tbody>
            <b-tr v-for="row in rows" :key="`row-${row.uid}`">
              <b-td>
                <!-- Una dimensión puede medir varios Indicadores: el modelo lo
                     permite (es un m2m) y una dimensión integradora los
                     necesita. Cada fila ofrece TODOS los Indicadores de la
                     evaluación, igual que el formulario manual: esconder los
                     que ya están en otra fila dejaba al selector con una sola
                     opción —la propia— y hacía imposible lo que el selector
                     múltiple viene a permitir. Agrupar o repetir es decisión
                     de quien arma la pauta. -->
                <!-- Donde se evalúa a través de la Dimensión, la dimensión
                     mide UN Indicador: selector simple, igual que
                     `RubricCriteriaForm.vue` en esa institución. -->
                <b-form-select
                  v-if="dimensionDriven"
                  :value="(row.criterias || [])[0] || null"
                  size="sm"
                  :disabled="busy"
                  @change="(value) => setSingleCriteria(row, value)"
                >
                  <b-form-select-option
                    v-for="criteria in criteriaOptions"
                    :key="`criteria-${row.uid}-${criteria.id}`"
                    :value="criteria.id"
                  >
                    {{ criteria.label }}
                  </b-form-select-option>
                </b-form-select>
                <b-form-select
                  v-else
                  v-model="row.criterias"
                  size="sm"
                  multiple
                  :select-size="criteriaSelectSize"
                  :disabled="busy"
                >
                  <b-form-select-option
                    v-for="criteria in criteriaOptions"
                    :key="`criteria-${row.uid}-${criteria.id}`"
                    :value="criteria.id"
                  >
                    {{ criteria.label }}
                  </b-form-select-option>
                </b-form-select>
              </b-td>
              <b-td>
                <b-form-input
                  v-model.number="row.weighing"
                  type="number"
                  size="sm"
                  min="0"
                  :max="rowMaxWeighing(row)"
                  step="1"
                  :disabled="busy"
                ></b-form-input>
                <!-- En DUOC el tope de una dimensión no es 100: es lo que su
                     Indicador pondera en el Instrumento, menos lo que ya se
                     llevan las otras dimensiones de ese mismo Indicador. Es la
                     regla de `RubricCriteriaForm.max_weighing`. -->
                <small
                  v-if="dimensionDriven && (row.criterias || []).length > 0"
                  :class="
                    (row.weighing || 0) > rowMaxWeighing(row)
                      ? 'text-danger'
                      : 'text-muted'
                  "
                >
                  {{
                    $t("reusable.aiRubricBuilderModal.weighingOfCriteria", {
                      total: rowMaxWeighing(row),
                    })
                  }}
                </small>
              </b-td>
              <b-td class="text-center">
                <b-button
                  variant="link"
                  size="sm"
                  class="p-0 kl-ai-row-remove"
                  :disabled="busy"
                  :title="$t('reusable.aiRubricBuilderModal.removeRow')"
                  @click="removeRow(row.uid)"
                >
                  <b-icon icon="trash"></b-icon>
                </b-button>
              </b-td>
            </b-tr>
            <b-tr v-if="rows.length === 0">
              <b-td colspan="3" class="text-center text-muted kl-ai-small">
                {{ $t("reusable.aiRubricBuilderModal.rowsEmpty") }}
              </b-td>
            </b-tr>
          </b-tbody>
        </b-table-simple>

        <div class="d-flex align-items-center justify-content-between">
          <div>
            <b-button
              variant="link"
              size="sm"
              class="p-0 mr-3"
              :disabled="busy || criteriaOptions.length === 0"
              @click="addRow"
            >
              <b-icon icon="plus" class="mr-1"></b-icon>
              {{ $t("reusable.aiRubricBuilderModal.addRow") }}
            </b-button>
            <b-button
              variant="link"
              size="sm"
              class="p-0"
              :disabled="busy"
              @click="distributeWeighing"
            >
              {{ $t("reusable.aiRubricBuilderModal.distribute") }}
            </b-button>
          </div>
          <small :class="weighingOff ? 'text-danger' : 'text-muted'">
            <!-- Al rellenar una pauta que ya tiene dimensiones, el 100% no está
                 libre: lo que falta es la diferencia. Se dice, porque si no el
                 total parece no cuadrar con las filas de arriba. -->
            <span v-if="existingWeighing > 0 && !dimensionDriven">
              {{
                $t("reusable.aiRubricBuilderModal.weighingExisting", {
                  count: existingCriteriaCount,
                  total: existingWeighing,
                })
              }}
              ·
            </span>
            {{
              $t("reusable.aiRubricBuilderModal.weighingTotal", {
                total: totalWeighing,
              })
            }}
          </small>
        </div>
      </template>

      <!-- ===================== Paso 2 ===================== -->
      <template v-else>
        <p class="kl-ai-small text-muted mb-3">
          <b-spinner
            v-if="generating"
            small
            variant="primary"
            class="mr-1"
          ></b-spinner>
          {{
            generating
              ? $t("reusable.aiRubricBuilderModal.streaming", {
                  done: proposed.length,
                  total: rows.length,
                })
              : $t("reusable.aiRubricBuilderModal.reviewHint", {
                  count: pendingItems.length,
                })
          }}
        </p>

        <b-table-simple
          v-if="proposed.length > 0"
          small
          bordered
          responsive
          class="kl-ai-matrix"
        >
          <b-thead>
            <b-tr>
              <!-- Dos columnas, los mismos nombres y en el mismo orden que la
                   pauta ya creada: la Dimensión es lo que escribió la IA y el
                   Indicador es lo que la dimensión mide. Con una sola columna
                   —rotulada como el Indicador y con la Dimensión dentro— el
                   paso 2 no se podía comparar con la pauta. -->
              <b-th style="width: 20%">{{ dimensionLabelPlural }}</b-th>
              <b-th style="width: 20%">{{ criteriaLabelPlural }}</b-th>
              <b-th v-for="level in levels" :key="`head-${level.order}`">
                {{ level.title }}
                <small class="text-muted">· {{ level.score }}</small>
              </b-th>
              <b-th style="width: 4%"></b-th>
            </b-tr>
          </b-thead>
          <b-tbody>
            <b-tr
              v-for="criteria in proposed"
              :key="`criteria-${criteria.ref}`"
              :class="{ 'kl-ai-row-discarded': criteria.discarded }"
            >
              <b-td>
                <div class="kl-ai-criteria-title">{{ criteria.title }}</div>
                <small class="text-muted">{{ criteria.weighing }}%</small>
              </b-td>
              <b-td class="kl-ai-small">
                <!-- La oración completa de cada Indicador, no sólo su número:
                     revisar si la dimensión mide lo que dice medir es
                     justamente comparar las dos cosas, y para eso hay que
                     tenerlas al lado. -->
                <div
                  v-for="sentence in criteriaSentences(criteria)"
                  :key="`ecm-${criteria.ref}-${sentence.id}`"
                  class="kl-ai-ecm"
                >
                  {{ sentence.label }}
                </div>
              </b-td>
              <b-td
                v-for="cell in criteria.cells"
                :key="`cell-${criteria.ref}-${cell.level_order}`"
                class="kl-ai-small"
              >
                {{ cell.description }}
              </b-td>
              <b-td class="text-center">
                <b-button
                  variant="link"
                  size="sm"
                  class="p-0 kl-ai-row-remove"
                  :disabled="busy"
                  :title="
                    criteria.discarded
                      ? $t('reusable.aiRubricBuilderModal.restore')
                      : $t('reusable.aiRubricBuilderModal.discard')
                  "
                  @click="toggleDiscard(criteria)"
                >
                  <b-icon
                    :icon="
                      criteria.discarded ? 'arrow-counterclockwise' : 'trash'
                    "
                  ></b-icon>
                </b-button>
              </b-td>
            </b-tr>
          </b-tbody>
        </b-table-simple>

        <p
          v-if="!generating && proposed.length === 0"
          class="text-center text-muted kl-ai-small"
        >
          {{ $t("reusable.aiRubricBuilderModal.reviewEmpty") }}
        </p>
      </template>

      <template #modal-footer>
        <div class="d-flex align-items-center justify-content-end w-100">
          <small v-if="saving" class="text-muted mr-auto">
            <b-spinner small variant="primary" class="mr-1"></b-spinner>
            {{
              $t("reusable.aiRubricBuilderModal.savingProgress", {
                done: saved_count,
                total: pendingItems.length,
              })
            }}
          </small>
          <b-button
            variant="secondary"
            size="sm"
            class="mr-2"
            :disabled="busy"
            @click="step === 'review' && !generating ? back() : close()"
          >
            {{
              step === "review" && !generating
                ? $t("reusable.aiRubricBuilderModal.back")
                : $t("reusable.aiRubricBuilderModal.close")
            }}
          </b-button>
          <b-button
            v-if="step === 'setup'"
            variant="primary"
            size="sm"
            :disabled="!canGenerate"
            @click="generate"
          >
            <img src="@/assets/icon-ai.svg" alt="" class="kl-ai-icon-sm mr-1" />
            {{
              $t("reusable.aiRubricBuilderModal.generate", {
                count: rows.length,
              })
            }}
          </b-button>
          <b-button
            v-else
            variant="primary"
            size="sm"
            :disabled="busy || pendingItems.length === 0"
            @click="save"
          >
            {{
              rubric
                ? $t("reusable.aiRubricBuilderModal.saveFill", {
                    count: pendingItems.length,
                  })
                : $t("reusable.aiRubricBuilderModal.save", {
                    count: pendingItems.length,
                  })
            }}
          </b-button>
        </div>
      </template>
    </b-modal>
  </span>
</template>

<script>
// E55 · Generar con IA la pauta de un Instrumento, desde lo que la pieza del
// Diseño Instruccional ya declaró.
//
// El reparto de trabajo es la decisión central, y no es la obvia:
//
//   - Las COLUMNAS (niveles de desempeño) salen de un Modelo de Pauta de la
//     institución. Son una escala institucional; si la IA las inventara, cada
//     pauta tendría la suya y los logros dejarían de ser comparables.
//   - Las FILAS son los Indicadores de Logro que la pieza del diseño declaró,
//     con la ponderación que el usuario les da acá.
//   - La IA escribe el criterio y las CELDAS, que es donde está el trabajo real:
//     decir qué distingue un nivel del siguiente para ese Indicador.
//
// Sirve para los dos casos con el mismo motor: crear la pauta completa, o
// rellenar una que ya existe con los Indicadores que todavía no tienen criterio.
// En el segundo caso la escala no se elige — son las columnas que esa pauta ya
// tiene.
import * as names from "@/store/names";
import { mapGetters } from "vuex";
import { toast } from "@/utils/utils";
import useAiBuilderStream from "@/components/reusable/ai-builder/useAiBuilderStream";
import { saveAiRubric } from "@/components/new-evaluations/Rubrics/createAiRubric";

export default {
  name: "AiRubricBuilderModal",
  components: {
    AiThemeInput: () =>
      import("@/components/reusable/ai-builder/AiThemeInput"),
  },
  props: {
    test_id: {
      type: Number,
      required: true,
    },
    // La pauta, si ya existe: entonces esto rellena en vez de crear.
    rubric: {
      type: Object,
      default: null,
    },
    evaluation_criteria_micro_ids: {
      type: Array,
      default: () => [],
    },
    piece_name: {
      type: String,
      default: "",
    },
    show_button: {
      type: Boolean,
      default: true,
    },
    button_class: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      visible: false,
      step: "setup",
      theme: "",
      rubric_title: "",
      preset_id: null,
      show_percentage_sign: false,
      rows: [],
      uid: 0,
      proposed: [],
      generating: false,
      saving: false,
      saved_count: 0,
      error: null,
      // Lo pone `save` y lo lee `confirmClose`: el cierre que viene DESPUÉS de
      // guardar no tiene nada que perder, así que no pregunta.
      skip_close_confirm: false,
    };
  },
  computed: {
    ...mapGetters({
      rubric_presets: names.RUBRIC_PRESETS,
      rubric_preset_units: names.RUBRIC_PRESET_UNITS,
      rubric_criterias: names.NEW_RUBRIC_CRITERIAS,
      rubric_achievements: names.NEW_RUBRIC_ACHIEVEMENTS,
      evaluation_criteria_micros: names.EVALUATION_CRITERIAS,
      study_units: names.STUDY_UNITS,
      competence_units: names.COMPETENCE_UNITS,
      test_evaluation_criteria_micros:
        names.NEW_TEST_EVALUATION_CRITERIA_MICROS,
    }),
    busy() {
      return this.generating || this.saving;
    },
    rubricLabel() {
      return this.$getVisibleNames("evaluations2.rubric", false, "Pauta");
    },
    criteriaLabel() {
      return this.$getVisibleNames(
        "teaching.evaluationcriteriamicro",
        false,
        "Indicador de Logro"
      );
    },
    // "manual.dimension" es el nombre con el que la pauta rotula esta columna;
    // en plural, igual que allá.
    dimensionLabelPlural() {
      return this.$getVisibleNames("manual.dimension", true, "Dimensiones");
    },
    criteriaLabelPlural() {
      return this.$getVisibleNames(
        "teaching.evaluationcriteriamicro",
        true,
        "Indicadores de Logro"
      );
    },
    presetOptions() {
      return this.rubric_presets.map((preset) => ({
        value: preset.id,
        text: preset.title,
      }));
    },
    preset() {
      return this.rubric_presets.find((x) => x.id == this.preset_id) || null;
    },
    presetUnits() {
      if (this.preset_id == null) return [];
      return this.rubric_preset_units
        .filter((x) => x.rubric_preset == this.preset_id)
        .sort((a, b) => a.order - b.order);
    },
    // Las columnas: del Modelo al crear, de la pauta misma al rellenar.
    levels() {
      if (this.rubric) {
        return this.rubric_achievements
          .filter((x) => x.rubric == this.rubric.id)
          .sort((a, b) => a.order - b.order)
          .map((level) => ({
            order: level.order,
            title: level.title,
            score: level.score,
          }));
      }
      return this.presetUnits.map((unit) => ({
        order: unit.order,
        title: unit.title,
        score: unit.score,
      }));
    },
    // Los Indicadores que la pauta ya mide: su fila queda tomada, para que
    // rellenar no agregue un segundo criterio sobre lo mismo —dos criterios
    // midiendo un Indicador reparten su logro en dos y eso no se ve.
    takenCriteriaIds() {
      if (!this.rubric) return [];
      const ids = [];
      for (const criteria of this.rubric_criterias.filter(
        (x) => x.rubric == this.rubric.id
      )) {
        if (criteria.evaluation_criteria_micro != null)
          ids.push(criteria.evaluation_criteria_micro);
        for (const id of criteria.evaluation_criterias_micro || []) ids.push(id);
      }
      return ids;
    },
    criteriaOptions() {
      return this.evaluation_criteria_micros
        .filter((x) => this.evaluation_criteria_micro_ids.includes(x.id))
        .map((criteria) => {
          const study_unit = this.study_units.find(
            (x) => x.id == criteria.study_unit
          );
          // Tres niveles —Unidad de Competencia, Unidad de Estudio,
          // Indicador—, que es como lo numeran el formulario manual
          // (`RubricCriteriaForm.vue`) y la pauta ya creada. Con dos niveles el
          // modal decía "1.1" donde la pauta dice "1.1.1" y el docente no
          // reconocía el Indicador que había elegido. Sin Unidad de Competencia
          // quedan dos, el mismo respaldo que hace la vista.
          const competence_unit = study_unit
            ? this.competence_units.find(
                (x) => x.id == study_unit.temp_competence_unit
              )
            : null;
          const unit_prefix = competence_unit
            ? `${competence_unit.order}.${study_unit.order}`
            : study_unit
            ? `${study_unit.order}`
            : "";
          const prefix = unit_prefix
            ? `${unit_prefix}.${criteria.order}`
            : `${criteria.order}`;
          return {
            id: criteria.id,
            competence_unit_order: competence_unit
              ? competence_unit.order
              : 999,
            study_unit_order: study_unit ? study_unit.order : 999,
            label: `${prefix} ${criteria.full_sentence}`,
          };
        })
        .sort((a, b) => {
          if (a.competence_unit_order !== b.competence_unit_order)
            return a.competence_unit_order - b.competence_unit_order;
          if (a.study_unit_order !== b.study_unit_order)
            return a.study_unit_order - b.study_unit_order;
          return a.label.localeCompare(b.label);
        });
    },
    // Las dos formas de mostrar el logro de cada nivel, con los mismos textos
    // que el formulario manual.
    showPercentageSigns() {
      return [
        {
          value: false,
          text: this.$t("evaluations.newEvaluations.score"),
        },
        {
          value: true,
          text: this.$t("evaluations.newEvaluations.percentage"),
        },
      ];
    },
    criteriaSelectSize() {
      return Math.min(6, Math.max(3, this.criteriaOptions.length));
    },
    // El mismo rotulo que usa el formulario manual, con el nombre alternativo de
    // la institucion: si alla dice "Niveles de Desempeno por", aca tambien.
    achievementByLabel() {
      return this.$t("evaluations.newEvaluations.entityBy", {
        entity: this.$getVisibleNames(
          "evaluations.observationachievement",
          false,
          "Niveles de Logro"
        ),
      });
    },
    // Los Indicadores que ya están puestos en alguna fila. No limitan lo que el
    // selector ofrece; sirven para proponer el Indicador de la fila que se
    // agrega, que es lo útil: la siguiente dimensión suele ser sobre lo que
    // todavía no se mide.
    usedCriteriaIds() {
      const ids = [];
      for (const row of this.rows)
        for (const id of row.criterias || []) ids.push(id);
      return ids;
    },
    unusedCriterias() {
      const used = this.usedCriteriaIds;
      const taken = this.takenCriteriaIds;
      return this.criteriaOptions.filter(
        (criteria) =>
          !used.includes(criteria.id) && !taken.includes(criteria.id)
      );
    },
    /**
     * La institución evalúa a través de la Dimensión.
     *
     * En DUOC la dimensión mide UN Indicador —la FK, no el m2m— y su
     * ponderación se acota por lo que ese Indicador pondera en el Instrumento.
     * Va por `internal_use_id` porque así están las demás condiciones del
     * módulo; el backend hace la misma pregunta por su lado.
     */
    dimensionDriven() {
      return !!(
        this.institution && this.institution.internal_use_id == "duoc.cl"
      );
    },
    // Lo que cada Indicador pondera en el Instrumento, { id: peso }. Es el
    // techo de las dimensiones que lo miden.
    criteriaTestWeighing() {
      const map = {};
      if (!this.test_id) return map;
      for (const row of this.test_evaluation_criteria_micros) {
        if (row.test != this.test_id) continue;
        map[row.evaluation_criteria_micro] =
          (map[row.evaluation_criteria_micro] || 0) + (row.weighing || 0);
      }
      return map;
    },
    // Lo que ya ponderan las dimensiones de la pauta. Al rellenar una pauta que
    // ya tiene filas, el 100% no está disponible: repartirlo de nuevo le daba
    // 100% a la dimensión nueva y dejaba la pauta sumando 200%.
    existingWeighing() {
      if (!this.rubric) return 0;
      return this.$round(
        this.rubric_criterias
          .filter((x) => x.rubric == this.rubric.id)
          .reduce((total, criteria) => total + (criteria.weighing || 0), 0)
      );
    },
    availableWeighing() {
      return Math.max(0, this.$round(100 - this.existingWeighing));
    },
    totalWeighing() {
      return this.$round(
        this.existingWeighing +
          this.rows.reduce((total, row) => total + (row.weighing || 0), 0)
      );
    },
    // Se avisa, no se bloquea: alguien puede estar armando la pauta por partes.
    //
    // Donde se evalúa a través de la Dimensión no se mide contra 100: la pauta
    // reparte lo que cada Indicador pondera en el Instrumento, así que lo que
    // se avisa es la fila que se pasó de SU techo.
    weighingOff() {
      if (this.rows.length === 0) return false;
      if (this.dimensionDriven)
        return this.rows.some(
          (row) => (row.weighing || 0) > this.rowMaxWeighing(row)
        );
      return this.totalWeighing !== 100;
    },
    // Hay trabajo que perder cuando la IA ya escribió algo, cuando está
    // escribiendo, o cuando se redactó un tema a mano.
    hasUnsavedWork() {
      return this.generating || this.proposed.length > 0 || !!this.theme;
    },
    pendingItems() {
      return this.proposed.filter((x) => !x.discarded);
    },
    canGenerate() {
      return (
        !this.busy &&
        this.rows.length > 0 &&
        this.levels.length > 0 &&
        // Una fila sin Indicador no sabría qué medir, y el backend la
        // descartaría en silencio: mejor no dejar generar.
        this.rows.every((row) => (row.criterias || []).length > 0)
      );
    },
    existingCriteriaCount() {
      if (!this.rubric) return 0;
      return this.rubric_criterias.filter((x) => x.rubric == this.rubric.id)
        .length;
    },
    // Detrás de la última dimensión, que no es lo mismo que detrás de la cuenta:
    // si se borró una, quedan 7 dimensiones con último orden 8 y la nueva
    // chocaría con una que ya existe.
    nextCriteriaOrder() {
      if (!this.rubric) return 1;
      return (
        this.rubric_criterias
          .filter((x) => x.rubric == this.rubric.id)
          .reduce((last, criteria) => Math.max(last, criteria.order || 0), 0) + 1
      );
    },
  },
  methods: {
    open() {
      this.reset();
      if (this.criteriaOptions.length === 0) {
        toast(
          this.$t("reusable.aiRubricBuilderModal.noCriterias", {
            entity: this.criteriaLabelPlural,
          })
        );
        return;
      }
      this.$store.dispatch(names.FETCH_RUBRIC_PRESETS);
      this.$store.dispatch(names.FETCH_RUBRIC_PRESET_UNITS);
      // El techo de cada dimensión sale de acá cuando se evalúa a través de
      // ella: sin esta lista el modal propondría 0% en todas las filas.
      if (this.dimensionDriven && this.test_id)
        this.$store.dispatch(names.FETCH_NEW_TEST_EVALUATION_CRITERIA_MICROS, {
          test_id: this.test_id,
        });
      if (!this.rubric && this.rubric_presets.length > 0)
        this.preset_id = this.rubric_presets[0].id;
      // El nombre empieza VACÍO, con su placeholder. Proponer el de la
      // evaluación era peor que no proponer nada: la pauta casi nunca se llama
      // como la evaluación, y un campo ya lleno se guarda sin leerlo. Si queda
      // vacío, `save` cae al nombre genérico de la entidad.

      // Una dimensión por Indicador que la pauta todavía no mide. Es el punto de
      // partida corriente; agrupar dos en una dimensión es editar una fila.
      const taken = this.takenCriteriaIds;
      this.rows = this.criteriaOptions
        .filter((criteria) => !taken.includes(criteria.id))
        .map((criteria) => this.newRow([criteria.id]));
      this.distributeWeighing();
      this.visible = true;
    },
    close() {
      this.visible = false;
    },
    back() {
      this.step = "setup";
      this.proposed = [];
      this.error = null;
    },
    reset() {
      this.step = "setup";
      this.theme = "";
      this.rubric_title = "";
      this.preset_id = null;
      this.show_percentage_sign = false;
      this.rows = [];
      this.proposed = [];
      this.generating = false;
      this.saving = false;
      this.saved_count = 0;
      this.error = null;
      this.skip_close_confirm = false;
    },
    newRow(criteria_ids) {
      this.uid += 1;
      return {
        uid: this.uid,
        criterias: criteria_ids || [],
        weighing: 0,
      };
    },
    addRow() {
      // Se propone el primer Indicador que todavía nadie mide, porque es lo que
      // suele faltar; si ya están todos, la fila nace vacía y la llena quien
      // sabe qué quiere agrupar.
      const first = this.unusedCriterias[0];
      this.rows.push(this.newRow(first ? [first.id] : []));
      this.distributeWeighing();
    },
    removeRow(uid) {
      this.rows = this.rows.filter((row) => row.uid !== uid);
      this.distributeWeighing();
    },
    /**
     * Pregunta antes de cerrar cuando hay algo que perder.
     *
     * Un clic fuera del modal cerraba el paso 2 con la matriz ya escrita y sin
     * guardar, y no había forma de recuperarla: hay que generarla de nuevo, con
     * su costo. Mismo gesto y mismos textos que la Matriz y el formulario de la
     * asignatura, para que la plataforma pregunte siempre igual.
     */
    confirmClose(event) {
      if (this.skip_close_confirm || !this.hasUnsavedWork) return;
      event.preventDefault();
      this.$bvModal
        .msgBoxConfirm(
          this.$t("evaluations.newEvaluations.sureCloseUnsavedData"),
          {
            title: this.$t("evaluations.newEvaluations.confirmationRequired"),
            size: "md",
            buttonSize: "md",
            okVariant: "warning",
            okTitle: this.$t("evaluations.newEvaluations.yesClose"),
            cancelTitle: this.$t("reusable.common.cancel"),
            footerClass: "p-2",
            hideHeaderClose: true,
            centered: true,
          }
        )
        .then((value) => {
          if (!value) return;
          this.skip_close_confirm = true;
          this.visible = false;
        });
    },
    /**
     * Reparte 100% entre los criterios elegidos.
     *
     * El resto se le suma al primero en vez de dejar decimales en todos: tres
     * criterios dan 33,33 cada uno y la pauta mostraría 99,99.
     */
    /**
     * El tope de ponderación de una fila.
     *
     * Donde se evalúa a través de la Dimensión: lo que su Indicador pondera en
     * el Instrumento, menos lo que ya se llevan las dimensiones existentes de
     * ese mismo Indicador y las otras filas del modal que lo miden. En el resto
     * de las instituciones es lo que queda libre de los 100 de la pauta.
     */
    rowMaxWeighing(row) {
      if (!this.dimensionDriven) return this.availableWeighing;
      const criteria_id = (row.criterias || [])[0];
      if (criteria_id == null) return 0;
      const total = this.criteriaTestWeighing[criteria_id] || 0;
      const taken_by_rubric = this.rubric
        ? this.rubric_criterias
            .filter(
              (x) =>
                x.rubric == this.rubric.id &&
                x.evaluation_criteria_micro == criteria_id
            )
            .reduce((sum, x) => sum + (x.weighing || 0), 0)
        : 0;
      const taken_by_rows = this.rows
        .filter(
          (other) =>
            other.uid !== row.uid && (other.criterias || [])[0] == criteria_id
        )
        .reduce((sum, other) => sum + (other.weighing || 0), 0);
      return Math.max(0, this.$round(total - taken_by_rubric - taken_by_rows));
    },
    // El selector simple escribe igual un arreglo: así la fila tiene una sola
    // forma y el resto del modal no se entera de la diferencia.
    setSingleCriteria(row, criteria_id) {
      row.criterias = criteria_id == null ? [] : [criteria_id];
      this.distributeWeighing();
    },
    distributeWeighing() {
      if (this.rows.length === 0) return;
      // Donde se evalúa a través de la Dimensión el reparto es POR INDICADOR:
      // las dimensiones que miden el mismo Indicador se parten su ponderación,
      // y las de Indicadores distintos no compiten entre sí.
      if (this.dimensionDriven) {
        const by_criteria = {};
        for (const row of this.rows) {
          const id = (row.criterias || [])[0];
          if (id == null) {
            row.weighing = 0;
            continue;
          }
          if (!by_criteria[id]) by_criteria[id] = [];
          by_criteria[id].push(row);
        }
        for (const id of Object.keys(by_criteria)) {
          const group = by_criteria[id];
          group.forEach((row) => (row.weighing = 0));
          const available = this.rowMaxWeighing(group[0]);
          const base = Math.floor(available / group.length);
          group.forEach((row, index) => {
            row.weighing =
              index === 0 ? available - base * (group.length - 1) : base;
          });
        }
        return;
      }
      // Sobre lo que queda libre, no sobre 100: al agregar una dimensión a una
      // pauta que ya pondera 100% esto reparte 0 y el usuario ajusta a mano
      // —que es correcto: para que entre una, otra tiene que bajar.
      const available = this.availableWeighing;
      const base = Math.floor(available / this.rows.length);
      this.rows.forEach((row, index) => {
        row.weighing =
          index === 0 ? available - base * (this.rows.length - 1) : base;
      });
    },
    toggleDiscard(criteria) {
      this.$set(criteria, "discarded", !criteria.discarded);
    },
    /**
     * Los Indicadores que mide una dimensión propuesta, con su número y su
     * oración completa.
     *
     * Los textos salen de `criteriaOptions`, que es la misma lista que el paso
     * 1 ofrece: así el Indicador se lee igual donde se elige y donde se
     * revisa. El que no esté en esa lista cae a su número, que es lo único
     * que se sabe de él.
     */
    criteriaSentences(criteria) {
      const ids = criteria.evaluation_criterias_micro || [];
      return ids.map((id, index) => {
        const option = this.criteriaOptions.find((x) => x.id == id);
        if (option) return { id, label: option.label };
        const numbers = (criteria.criteria_label || "").split(", ");
        return { id, label: numbers[index] || "" };
      });
    },
    async generate() {
      this.error = null;
      this.proposed = [];
      this.step = "review";
      this.generating = true;
      const { callBuilder } = useAiBuilderStream(
        {
          url: "/common/evaluation-instrument-builder-ai/",
          generate_error_key: "reusable.aiRubricBuilderModal.generateError",
        },
        this
      );
      try {
        const payload = {
          test: this.test_id,
          target: "rubric",
          theme: this.theme || "",
          criteria: this.rows.map((row) => ({
            evaluation_criterias_micro: row.criterias,
            weighing: row.weighing,
          })),
        };
        // La escala: la pauta cuando ya existe, el Modelo cuando se va a crear.
        if (this.rubric) payload.rubric = this.rubric.id;
        else payload.rubric_preset = this.preset_id;

        const result = await callBuilder(payload, (event) => {
          if (event.type === "criteria" && event.item) {
            this.proposed.push({ ...event.item, discarded: false });
          }
        });
        // El `done` es autoritativo: reemplaza lo que llegó en vivo, que pudo
        // incluir un criterio que el validador final descartó por una celda
        // faltante.
        this.proposed = ((result || {}).criterias || []).map((criteria) => ({
          ...criteria,
          discarded: false,
        }));
        if (this.proposed.length === 0) {
          this.error = this.$t("reusable.aiRubricBuilderModal.generateEmpty");
        }
      } catch (error) {
        this.error =
          error.message ||
          this.$t("reusable.aiRubricBuilderModal.generateError");
      } finally {
        this.generating = false;
      }
    },
    async save() {
      const criterias = this.pendingItems;
      if (criterias.length === 0) return;
      this.saving = true;
      this.saved_count = 0;
      try {
        // Al rellenar, los niveles son los que la pauta ya tiene: el mapa
        // order → id es lo que cuelga cada celda de su columna.
        const levels_by_order = {};
        if (this.rubric) {
          for (const level of this.rubric_achievements.filter(
            (x) => x.rubric == this.rubric.id
          ))
            levels_by_order[level.order] = level.id;
        }

        const { rubric, created } = await saveAiRubric(this, {
          test_id: this.test_id,
          rubric: this.rubric,
          title: this.rubric_title || this.rubricLabel,
          show_percentage_sign: this.show_percentage_sign,
          preset: this.preset,
          preset_units: this.presetUnits,
          levels_by_order: this.rubric ? levels_by_order : null,
          criterias,
          single_criteria: this.dimensionDriven,
          start_order: this.nextCriteriaOrder,
          on_saved: (count) => {
            this.saved_count = count;
          },
        });
        if (created.length === 0) {
          this.error = this.$t("reusable.aiRubricBuilderModal.generateError");
          return;
        }
        toast(
          this.$t("reusable.aiRubricBuilderModal.saved", {
            count: created.length,
          })
        );
        this.$emit("created", { rubric, created });
        this.skip_close_confirm = true;
        this.visible = false;
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
/* El morado de la familia de botones de creación rápida: se leen como un mismo
   gesto, no como uno por vista. */
.btn-ai-quick {
  background-color: var(--kl-fourth-hover-color) !important;
  border-color: var(--kl-fourth-hover-color) !important;
  color: var(--kl-text-on-primary) !important;
}
.btn-ai-quick:hover {
  opacity: 0.9;
}
.kl-ai-icon-sm {
  width: 14px;
  height: 14px;
  filter: brightness(0) invert(1);
}
.kl-ai-icon-lg {
  width: 22px;
  height: 22px;
  margin-top: 2px;
}

/* El cuerpo y el pie los dibuja b-modal, así que su marcado no lleva el scope
   de este componente: sin ::v-deep estas reglas no llegarían. */
::v-deep .kl-ai-dialog {
  max-width: min(1200px, calc(100vw - 2rem));
}
::v-deep .kl-ai-body {
  max-height: 70vh;
  overflow-y: auto;
  text-align: left;
}
::v-deep .kl-ai-footer {
  background: var(--kl-surface-muted);
}

.kl-ai-title {
  font-weight: var(--kl-fw-bold);
  color: var(--kl-text-heading);
}
.kl-ai-close {
  color: var(--kl-text-muted);
  line-height: 1;
}
.kl-ai-steps {
  display: flex;
  gap: 4px;
}
.kl-ai-step {
  height: 3px;
  flex: 1;
  border-radius: var(--kl-radius-pill);
  background: var(--kl-border-soft);
  transition: background 0.2s ease;
}
.kl-ai-step-done {
  background: var(--kl-color-primary);
}
.kl-ai-label {
  font-weight: var(--kl-fw-semibold);
  color: var(--kl-text-heading);
}
.kl-ai-small {
  font-size: var(--kl-fs-small);
}
.kl-ai-context {
  border-left: 3px solid var(--kl-color-primary);
  padding-left: 0.6rem;
}
.kl-ai-chip {
  display: inline-block;
  border: 1px solid var(--kl-border-soft);
  border-radius: var(--kl-radius-pill);
  padding: 0 0.5rem;
  margin: 0 0.25rem 0.25rem 0;
  background: var(--kl-surface-muted);
}
.kl-ai-table ::v-deep th,
.kl-ai-matrix ::v-deep th {
  font-size: var(--kl-fs-small);
  font-weight: var(--kl-fw-semibold);
  background: var(--kl-surface-muted);
}
.kl-ai-matrix ::v-deep td {
  vertical-align: top;
}
.kl-ai-criteria-title {
  font-weight: var(--kl-fw-semibold);
  color: var(--kl-text-heading);
  font-size: var(--kl-fs-small);
}
/* Un Indicador por linea: dos oraciones largas pegadas se leen como una. */
.kl-ai-ecm + .kl-ai-ecm {
  margin-top: 0.35rem;
  padding-top: 0.35rem;
  border-top: 1px solid var(--kl-border-soft);
}
.kl-ai-row-discarded {
  opacity: 0.45;
}
.kl-ai-row-remove {
  color: var(--kl-text-muted);
}
</style>
